import { addIndex, map } from 'ramda';
import React, { FC } from 'react';
import styled from 'styled-components';
import 'swiper/css';
import { Swiper, SwiperSlide } from 'swiper/react';

import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import FlexColumn from '@ui/atoms/FlexColumn';
import FlexRow from '@ui/atoms/FlexRow';
import SubheadingText from '@ui/atoms/SubheadingText';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';
import IntakeStepCard, { IIntakeStepCard } from '@ui/molecules/IntakeStepCard';

import usePrefersReducedMotion from '@hooks/usePrefersReducedMotion';
import useWindowSize from '@hooks/useWindowSize';

import { commonLabels } from '@utils/button';
import { doesNotExist } from '@utils/general';

import { IFlowStepComponentProps } from '@folx-types/flow-steps';

import theme from '@theme/FolxTheme';

export interface IIntakeIntroStepPageContent {
	intakeCards: IIntakeStepCard[];
	pageInfo: string;
	pageHeader: string;
}

interface IIntakeIntroStepProps extends IFlowStepComponentProps {
	pageContent: IIntakeIntroStepPageContent;
}

const PageColumn = styled(FlexColumn)`
	margin-bottom: ${theme.spacing.xxxl};

	@media ${theme.responsive.breakpoints.laptopM} {
		align-items: center;
		text-align: center;
	}
`;

const CenteredColumn = styled(FlexColumn)`
	align-items: center;
`;

const HeaderColumn = styled(CenteredColumn)`
	text-align: center;

	& > ${SubheadingText} {
		max-width: 32rem;
	}
`;

const ContinueButton = styled(Button)`
	align-self: center;
	max-width: 30rem;
`;

const IntakeIntroStep: FC<IIntakeIntroStepProps> = ({
	pageContent,
	handleNavNext = () => {
		return;
	},
	stepKey,
}) => {
	const { pageHeader, pageInfo, intakeCards } = pageContent;

	const mapIndexed = addIndex(map);
	const { isMobile } = useWindowSize(theme.responsive.sizes.laptopM);
	const shouldAnimate = !usePrefersReducedMotion();

	const intakeSwiper = () => {
		if (doesNotExist(intakeCards)) {
			return;
		}

		const swiperCards = mapIndexed((card: IIntakeStepCard, index) => {
			const stepNumber = index + 1;
			return (
				<SwiperSlide style={{ width: 'auto' }} key={stepNumber}>
					<IntakeStepCard {...card} stepNumber={stepNumber} />
				</SwiperSlide>
			);
		}, intakeCards);

		const intakeCardRow = mapIndexed((card: IIntakeStepCard, index) => {
			const stepNumber = index + 1;
			return <IntakeStepCard {...card} stepNumber={stepNumber} key={stepNumber} />;
		}, intakeCards);

		return isMobile ? (
			shouldAnimate ? (
				<Swiper
					slidesPerView={'auto'}
					spaceBetween={16}
					slidesOffsetAfter={32}
					slidesOffsetBefore={32}
					slideToClickedSlide
					style={{ marginRight: '-3.2rem', marginLeft: '-3.2rem', overflow: 'visible' }}>
					{swiperCards}
				</Swiper>
			) : (
				<CenteredColumn gap="sm">{intakeCardRow}</CenteredColumn>
			)
		) : (
			<FlexRow gap="sm">{intakeCardRow}</FlexRow>
		);
	};

	return (
		<CenteredGridItem>
			<PageColumn gap="lgxl">
				<HeaderColumn gap="xs">
					<BasicText>{pageHeader}</BasicText>
					<SubheadingText>{pageInfo}</SubheadingText>
				</HeaderColumn>
				{intakeSwiper()}
				<ContinueButton
					variant="secondary"
					label={commonLabels.continue}
					onClick={() => handleNavNext(stepKey)}
				/>
			</PageColumn>
		</CenteredGridItem>
	);
};

export default IntakeIntroStep;
